import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, asArray, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, PackageCheck, Wrench, TerminalSquare, Monitor, Cpu, UserX, KeyRound, LogOut, FolderArchive, Trash2, Lock, Download, AlertTriangle, Copy, Boxes, Building2, Play, BatteryFull, BatteryLow, BatteryCharging, HeartPulse } from "lucide-react";
import { StatusBadge } from "@/components/Primitives";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TABS = [
  ["overview", "Overview", Cpu],
  ["compliance", "Compliance", ShieldCheck],
  ["patches", "Patches", PackageCheck],
  ["onboard", "Onboarding", Boxes],
  ["terminal", "Terminal", TerminalSquare],
  ["screen", "Remote Desktop", Monitor],
  ["offboard", "Offboarding", UserX],
];

export default function AgentDetail() {
  const { id } = useParams();
  const [agent, setAgent] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    try { const { data } = await api.get(`/agent/${id}`); setAgent(data); }
    catch { toast.error("Agent not found"); }
  };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [id]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/agents" className="text-zinc-500 hover:text-zinc-200"><ArrowLeft size={18} /></Link>
        <h1 className="font-display text-2xl font-semibold tracking-tight">{agent?.hostname || id}</h1>
        {agent && <StatusBadge status={agent.online ? "online" : "offline"} />}
        <span className="ml-auto font-mono text-xs text-zinc-600">{id}</span>
      </div>

      <div className="flex gap-1 border-b border-zinc-900">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={"flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px " +
              (tab === key ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-200")}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview agent={agent} />}
      {tab === "compliance" && <Compliance id={id} online={agent?.online} />}
      {tab === "patches" && <Patches id={id} online={agent?.online} />}
      {tab === "onboard" && <Onboard id={id} agent={agent} online={agent?.online} reload={load} />}
      {tab === "terminal" && <Terminal id={id} online={agent?.online} />}
      {tab === "screen" && <Screen id={id} online={agent?.online} />}
      {tab === "offboard" && <Offboard id={id} online={agent?.online} />}
    </div>
  );
}

function Card({ title, children, right }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
        <div className="font-display font-medium text-sm">{title}</div>{right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ k, v }) {
  return <div className="flex justify-between py-1.5 border-b border-zinc-900/60 text-sm">
    <span className="text-zinc-500">{k}</span><span className="font-mono text-zinc-200">{v ?? "—"}</span></div>;
}

function fmtRuntime(min) {
  if (!min || min <= 0) return null;
  const h = Math.floor(min / 60), mm = min % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
}

function BatteryMetric({ battery }) {
  if (!battery || battery.percent == null) return null; // desktop / no battery
  const pct = Math.max(0, Math.min(100, Number(battery.percent)));
  const charging = !!battery.charging;
  const remaining = fmtRuntime(battery.minutes_remaining);
  const color = charging ? "#10b981" : pct <= 15 ? "#ef4444" : pct <= 30 ? "#f59e0b" : "#10b981";
  const Icon = charging ? BatteryCharging : pct <= 15 ? BatteryLow : BatteryFull;

  const health = battery.health_percent != null ? Math.round(Number(battery.health_percent)) : null;
  const healthColor = health == null ? "#71717a" : health >= 80 ? "#10b981" : health >= 60 ? "#f59e0b" : "#ef4444";
  return (
    <div className="mb-1 pt-3 border-t border-zinc-900/60" data-testid="agent-battery">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-zinc-500 uppercase flex items-center gap-1.5"><Icon size={14} style={{ color }} /> Battery</span>
        <span className="font-mono">
          {pct}%
          <span className="text-zinc-500 ml-2">{charging ? "charging" : remaining ? `${remaining} left` : "on battery"}</span>
        </span>
      </div>
      <div className="h-1.5 bg-zinc-900 rounded"><div className="h-full rounded" style={{ width: `${pct}%`, background: color }} /></div>

      {(health != null || battery.cycles != null || battery.condition) && (
        <div className="mt-2" data-testid="agent-battery-health">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-zinc-500 flex items-center gap-1.5"><HeartPulse size={12} style={{ color: healthColor }} /> Battery health</span>
            <span className="font-mono text-zinc-300">
              {health != null ? `${health}%` : "—"}
              {battery.condition && <span className="text-zinc-500 ml-2">{battery.condition}</span>}
            </span>
          </div>
          {health != null && (
            <div className="h-1.5 bg-zinc-900 rounded"><div className="h-full rounded" style={{ width: `${Math.min(100, health)}%`, background: healthColor }} /></div>
          )}
          {battery.cycles != null && <div className="text-[11px] text-zinc-500 font-mono mt-1">{battery.cycles} charge cycles</div>}
        </div>
      )}
    </div>
  );
}

function Overview({ agent }) {
  const m = agent?.metrics || {};
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Card title="System">
        <Field k="Hostname" v={agent?.hostname} />
        <Field k="OS" v={`${agent?.os || ""} ${agent?.os_version || ""}`} />
        <Field k="Serial" v={agent?.serial_number} />
        <Field k="Vendor" v={[agent?.manufacturer, agent?.model].filter(Boolean).join(" ")} />
        <Field k="Agent version" v={agent?.version} />
        <Field k="Last heartbeat" v={agent?.last_heartbeat?.slice(11, 19)} />
      </Card>
      <Card title="Live metrics">
        {["cpu", "mem", "disk"].map((key) => (
          <div key={key} className="mb-3">
            <div className="flex justify-between text-sm mb-1"><span className="text-zinc-500 uppercase">{key}</span><span className="font-mono">{m[key] ?? "—"}%</span></div>
            <div className="h-1.5 bg-zinc-900 rounded"><div className="h-full rounded" style={{ width: `${Math.min(100, m[key] || 0)}%`, background: (m[key] || 0) >= 90 ? "#ef4444" : (m[key] || 0) >= 75 ? "#f59e0b" : "#10b981" }} /></div>
          </div>
        ))}
        <BatteryMetric battery={m.battery} />
      </Card>
    </div>
  );
}

function sevColor(s) {
  return { critical: "text-red-400", high: "text-orange-400", medium: "text-amber-400", low: "text-emerald-400" }[s] || "text-zinc-400";
}

function Compliance({ id, online }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { const { data } = await api.get(`/agent/${id}/compliance`); setData(data); };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [id]);

  const scan = async () => { setBusy(true); try { await api.post(`/agent/${id}/compliance/scan`); toast.success("Compliance scan requested"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const remediate = async (controlId) => { try { const r = await api.post(`/agent/${id}/remediate?control_id=${encodeURIComponent(controlId)}`); toast.success(`Remediation ${r.data.delivered ? "dispatched" : "queued"}`); } catch (e) { toast.error(e.response?.data?.detail || "No remediation available"); } };

  const controls = data?.controls || [];
  return (
    <Card title={`Compliance audit ${data?.score != null ? `· ${data.score}%` : ""}`}
      right={<button onClick={scan} disabled={busy || !online} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs rounded px-3 py-1.5">Run scan</button>}>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left py-2">Control</th><th className="text-left">Title</th><th className="text-left">Severity</th><th className="text-left">Status</th><th></th></tr></thead>
        <tbody>
          {controls.map((c) => (
            <tr key={c.control_id} className="border-t border-zinc-900/60">
              <td className="py-2 font-mono text-xs">{c.control_id}</td>
              <td className="text-zinc-300">{c.title}</td>
              <td className={"text-xs " + sevColor(c.severity)}>{c.severity}</td>
              <td className={"text-xs font-medium " + (c.status === "pass" ? "text-emerald-400" : c.status === "fail" ? "text-red-400" : "text-zinc-500")}>{c.status}</td>
              <td className="text-right">{c.status === "fail" && <button onClick={() => remediate(c.control_id)} className="flex items-center gap-1 ml-auto text-xs text-blue-400 hover:text-blue-300"><Wrench size={12} />Remediate</button>}</td>
            </tr>
          ))}
          {controls.length === 0 && <tr><td colSpan={5} className="py-6 text-zinc-500">No compliance data — run a scan.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function Patches({ id, online }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [kb, setKb] = useState("");
  const load = async () => { const { data } = await api.get(`/agent/${id}/patches`); setData(data); };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [id]);

  const scan = async () => { try { await api.post(`/agent/${id}/patch/scan`); toast.success("Patch scan requested (~30s)"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const install = async () => { const ids = Object.keys(sel).filter((k) => sel[k]); if (!ids.length) return; try { const r = await api.post(`/agent/${id}/patch/install`, { update_ids: ids, reboot_allowed: false }); toast.success(`Install job ${r.data.job_id}`); setSel({}); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const rollback = async () => { if (!kb) return; try { const r = await api.post(`/agent/${id}/patch/uninstall`, { kb }); toast.success(`Rollback job ${r.data.job_id}`); setKb(""); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const updates = data?.updates || [];
  return (
    <Card title={`Available updates (${updates.length})`}
      right={<div className="flex items-center gap-2">
        <input value={kb} onChange={(e) => setKb(e.target.value)} placeholder="KB5030213" className="w-28 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs font-mono" />
        <button onClick={rollback} disabled={!kb || !online} className="bg-zinc-800 disabled:opacity-40 text-zinc-200 text-xs rounded px-3 py-1.5">Rollback</button>
        <button onClick={scan} disabled={!online} className="bg-zinc-800 text-zinc-200 text-xs rounded px-3 py-1.5">Scan</button>
        <button onClick={install} disabled={!Object.values(sel).some(Boolean)} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs rounded px-3 py-1.5">Install selected</button>
      </div>}>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th></th><th className="text-left py-2">KB</th><th className="text-left">Severity</th><th className="text-left">Size</th><th className="text-left">Title</th></tr></thead>
        <tbody>
          {updates.map((u) => (
            <tr key={u.update_id} className="border-t border-zinc-900/60">
              <td className="py-2"><input type="checkbox" checked={!!sel[u.update_id]} onChange={(e) => setSel({ ...sel, [u.update_id]: e.target.checked })} /></td>
              <td className="font-mono text-xs">{u.kb || u.update_id.slice(0, 8)}</td>
              <td className="text-xs text-zinc-400">{u.severity || "—"}</td>
              <td className="font-mono text-xs">{u.size_mb} MB</td>
              <td className="text-zinc-300">{u.title}</td>
            </tr>
          ))}
          {updates.length === 0 && <tr><td colSpan={5} className="py-6 text-zinc-500">No updates — run a scan.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function Onboard({ id, agent, online, reload }) {
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => { setClientId(agent?.client_id || ""); setSiteId(agent?.site_id || ""); }, [agent?.client_id, agent?.site_id]);
  useEffect(() => {
    api.get("/clients").then(r => setCompanies(asArray(r.data))).catch(() => {});
    api.get("/install-templates").then(r => setTemplates(asArray(r.data))).catch(() => {});
  }, []);
  useEffect(() => { if (!clientId) { setBranches([]); return; } api.get("/sites", { params: { client_id: clientId } }).then(r => setBranches(asArray(r.data))).catch(() => setBranches([])); }, [clientId]);
  const loadJobs = () => api.get(`/agent/${id}/onboard/jobs`).then(r => setJobs(asArray(r.data.jobs))).catch(() => {});
  useEffect(() => { loadJobs(); const t = setInterval(loadJobs, 4000); return () => clearInterval(t); }, [id]);

  const company = companies.find(c => c.id === (agent?.client_id || clientId));
  const assignedTplIds = company?.onboarding_template_ids || [];
  const assignedTemplates = templates.filter(t => assignedTplIds.includes(t.id));
  const plannedCount = assignedTemplates.reduce((n, t) => n + (t.items || []).length, 0);

  const assign = async () => {
    setBusy("assign");
    try { await api.post(`/agent/${id}/assign`, { client_id: clientId || null, site_id: siteId || null }); toast.success("Endpoint assigned"); reload && reload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const runOnboard = async () => {
    setBusy("onboard");
    try { const { data } = await api.post(`/agent/${id}/onboard`, {}); toast.success(`Installing ${data.count} app(s) from ${data.templates.join(", ") || "template"}`); loadJobs(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const latest = jobs[0];
  return (
    <div className="space-y-5">
      <Card title="Assign endpoint to a company">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Company</div>
            <select value={clientId} onChange={e => setClientId(e.target.value)} data-testid="onb-company" className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
              <option value="">Unassigned</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Branch / Location</div>
            <select value={siteId} onChange={e => setSiteId(e.target.value)} disabled={!clientId} data-testid="onb-branch" className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm disabled:opacity-50">
              <option value="">{clientId ? "No specific branch" : "Select a company first"}</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button onClick={assign} disabled={busy === "assign"} data-testid="onb-assign" className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded px-4 py-2">{busy === "assign" ? "Assigning…" : "Assign endpoint"}</button>
          {agent?.client_id && <span className="text-xs text-emerald-400">Currently: {companies.find(c => c.id === agent.client_id)?.name || agent.client_id}{agent.site_id ? " · " + (branches.find(b => b.id === agent.site_id)?.name || "branch") : ""}</span>}
        </div>
      </Card>

      <Card title="Install the company software set"
        right={<button onClick={runOnboard} disabled={!online || !agent?.client_id || plannedCount === 0 || busy === "onboard"} data-testid="onb-run" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs rounded px-3 py-1.5"><Play size={13} />{busy === "onboard" ? "Dispatching…" : `Run onboarding (${plannedCount})`}</button>}>
        {!agent?.client_id ? <div className="text-sm text-zinc-500">Assign this endpoint to a company first.</div>
          : assignedTemplates.length === 0 ? <div className="text-sm text-zinc-500">No software templates assigned to <span className="text-zinc-300">{company?.name}</span>. Assign one on the company page (Clients → {company?.name} → Onboarding Software).</div>
          : (
            <div className="space-y-3">
              {!online && <div className="text-xs text-amber-400">Agent offline — it must be online to install software.</div>}
              {assignedTemplates.map(t => (
                <div key={t.id}>
                  <div className="flex items-center gap-2 text-sm text-zinc-200"><Boxes size={14} className="text-blue-400" />{t.name} <span className="text-[11px] text-zinc-500 font-mono">{(t.items || []).length} apps</span></div>
                  <div className="flex flex-wrap gap-1 mt-1 pl-6">
                    {(t.items || []).map((i, idx) => <span key={idx} className="text-[10px] bg-zinc-900 border border-zinc-800 rounded px-2 py-0.5 text-zinc-400">{i.name || i.package}</span>)}
                  </div>
                </div>
              ))}
            </div>
          )}
      </Card>

      {latest && (
        <Card title={`Latest onboarding — ${latest.status}${latest.total ? ` · ${latest.installed || 0}/${latest.total} installed` : ""}`}>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left py-2">App</th><th className="text-left">Method</th><th className="text-left">Status</th><th className="text-left">Detail</th></tr></thead>
            <tbody>
              {(latest.items || []).map((it, i) => (
                <tr key={i} className="border-t border-zinc-900/60">
                  <td className="py-2">{it.name}</td>
                  <td className="text-xs text-zinc-400">{it.method}</td>
                  <td className={"text-xs font-medium " + (it.status === "ok" ? "text-emerald-400" : it.status === "error" ? "text-red-400" : it.status === "skipped" ? "text-zinc-500" : "text-amber-400")}>{it.status}</td>
                  <td className="text-xs text-zinc-500 max-w-sm truncate" title={it.output}>{it.output || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function Offboard({ id, online }) {
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState("");
  const [secret, setSecret] = useState(null);
  const [data, setData] = useState({ jobs: [], artifacts: [] });

  const load = async () => {
    try { const { data } = await api.get(`/agent/${id}/offboard/jobs`); setData({ jobs: asArray(data.jobs), artifacts: asArray(data.artifacts) }); }
    catch { /* non-privileged users can't list; ignore */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [id]);

  const run = async (op, extra = {}) => {
    if (!username.trim()) { toast.error("Enter the target account username first"); return; }
    setBusy(op);
    try {
      const { data } = await api.post(`/agent/${id}/offboard`, { op, username: username.trim(), ...extra });
      if (data.new_password) setSecret({ label: `New password for ${username.trim()}`, value: data.new_password });
      toast.success(`${OPS[op].label} dispatched`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const confirmRun = (op) => {
    const o = OPS[op];
    if (!window.confirm(`${o.confirm}\n\nTarget account: "${username.trim()}" on this endpoint.`)) return;
    run(op, op === "wipe_user" ? { confirm: true } : {});
  };

  const dlUrl = (aid) => `${BACKEND}/api/agent/offboard/artifact/${aid}/download`;

  return (
    <div className="space-y-5">
      <div className="bg-amber-950/20 border border-amber-600/30 rounded-md px-4 py-3 flex items-start gap-3">
        <AlertTriangle size={16} className="text-amber-400 mt-0.5" />
        <div className="text-sm text-amber-200/90">
          Remote offboarding acts on a <strong>single user account</strong> on this endpoint — it never wipes the whole device. Actions are logged and require an online agent. Restricted to security &amp; service-desk managers.
        </div>
      </div>

      {secret && (
        <div className="bg-emerald-950/30 border border-emerald-600/30 rounded-md p-4 flex items-start gap-3">
          <KeyRound size={16} className="text-emerald-400 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono mb-1">{secret.label} — shown once</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-zinc-900/60 border border-zinc-800 rounded p-2 font-mono text-[12px] break-all" data-testid="ofb-secret">{secret.value}</code>
              <button onClick={() => { navigator.clipboard.writeText(secret.value); toast.success("Copied"); }} className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-zinc-300"><Copy size={14} /></button>
            </div>
          </div>
          <button onClick={() => setSecret(null)} className="text-zinc-500 hover:text-zinc-200 text-xs">Dismiss</button>
        </div>
      )}

      <Card title="Offboard a user">
        <div className="flex items-center gap-2 mb-4">
          <label className="text-sm text-zinc-400 w-40">Target account username</label>
          <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. jsmith or DOMAIN\jsmith" data-testid="ofb-username" className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-500/60" />
        </div>
        {!online && <div className="text-xs text-amber-400 mb-3">Agent is offline — offboarding actions require the endpoint to be online.</div>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <OfbBtn icon={Lock} label="Lock / disable account" hint="Disable login + force sign-out" onClick={() => run("lock_account")} busy={busy === "lock_account"} disabled={!online} testid="ofb-lock" />
          <OfbBtn icon={KeyRound} label="Reset password" hint="Rotate to a new random password" onClick={() => run("reset_password")} busy={busy === "reset_password"} disabled={!online} testid="ofb-reset" />
          <OfbBtn icon={LogOut} label="Force logoff" hint="End the user's active session" onClick={() => run("logoff_user")} busy={busy === "logoff_user"} disabled={!online} testid="ofb-logoff" />
          <OfbBtn icon={FolderArchive} label="Extract user data" hint="Zip profile folders → upload here" onClick={() => run("collect_data")} busy={busy === "collect_data"} disabled={!online} testid="ofb-collect" />
          <OfbBtn icon={Trash2} label="Wipe user data" hint="Remove profile + disable account" danger onClick={() => confirmRun("wipe_user")} busy={busy === "wipe_user"} disabled={!online} testid="ofb-wipe" />
        </div>
      </Card>

      <Card title="Collected data (extracts)">
        {data.artifacts.length === 0 ? <div className="text-sm text-zinc-500">No archives yet. Run <span className="text-zinc-300">Extract user data</span> to collect the user's files.</div> : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left py-2">File</th><th className="text-left">Size</th><th className="text-left">Collected</th><th></th></tr></thead>
            <tbody>
              {data.artifacts.map((a) => (
                <tr key={a.id} className="border-t border-zinc-900/60">
                  <td className="py-2 font-mono text-xs">{a.filename}</td>
                  <td className="font-mono text-xs">{Math.max(1, Math.round((a.size || 0) / 1024))} KB</td>
                  <td className="font-mono text-xs text-zinc-500">{a.at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="text-right"><a href={dlUrl(a.id)} target="_blank" rel="noreferrer" data-testid={`ofb-dl-${a.id}`} className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"><Download size={13} /> Download</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Offboarding activity">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left py-2">Action</th><th className="text-left">User</th><th className="text-left">Status</th><th className="text-left">Result</th><th className="text-left">When</th></tr></thead>
          <tbody>
            {data.jobs.map((j) => (
              <tr key={j.job_id} className="border-t border-zinc-900/60 align-top">
                <td className="py-2">{OPS[j.op]?.label || j.op}</td>
                <td className="font-mono text-xs">{j.username}</td>
                <td className={"text-xs font-medium " + (j.status === "ok" ? "text-emerald-400" : j.status === "error" ? "text-red-400" : "text-amber-400")}>{j.status}</td>
                <td className="text-xs text-zinc-400 max-w-xs truncate" title={j.output}>{j.output || "—"}</td>
                <td className="font-mono text-xs text-zinc-500">{j.requested_at?.slice(11, 19)}</td>
              </tr>
            ))}
            {data.jobs.length === 0 && <tr><td colSpan={5} className="py-6 text-zinc-500">No offboarding actions yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const OPS = {
  lock_account: { label: "Lock account" },
  reset_password: { label: "Reset password" },
  logoff_user: { label: "Force logoff" },
  collect_data: { label: "Extract user data" },
  wipe_user: { label: "Wipe user data", confirm: "This permanently removes the user's profile and files and disables the account. This cannot be undone. Extract their data first if needed." },
};

function OfbBtn({ icon: Icon, label, hint, onClick, busy, disabled, danger, testid }) {
  return (
    <button onClick={onClick} disabled={disabled || busy} data-testid={testid}
      className={"text-left p-3 rounded border transition-colors disabled:opacity-40 " +
        (danger ? "bg-red-950/20 border-red-600/30 hover:border-red-500/50" : "bg-zinc-900/40 border-zinc-800 hover:border-zinc-700")}>
      <div className="flex items-center gap-2">
        <Icon size={15} className={danger ? "text-red-400" : "text-blue-400"} />
        <span className={"text-sm font-medium " + (danger ? "text-red-200" : "text-zinc-100")}>{busy ? "Working…" : label}</span>
      </div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{hint}</div>
    </button>
  );
}

function wsUrl(path) {
  const base = (BACKEND || window.location.origin).replace(/^http/, "ws");
  return base + "/api" + path;
}
async function ticket() { const { data } = await api.post("/agent/ws-ticket"); return data.ticket; }

function Terminal({ id, online }) {
  const [lines, setLines] = useState("");
  const [cmd, setCmd] = useState("");
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const outRef = useRef(null);

  useEffect(() => {
    if (!online) return;
    let ws;
    (async () => {
      const t = await ticket();
      ws = new WebSocket(wsUrl(`/agent/${id}/terminal?shell=cmd&token=${encodeURIComponent(t)}`));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onmessage = (e) => setLines((p) => (p + e.data).slice(-60000));
    })();
    return () => ws && ws.close();
  }, [id, online]);
  useEffect(() => { if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight; }, [lines]);

  const send = (e) => { e.preventDefault(); if (wsRef.current?.readyState === 1) { wsRef.current.send(cmd + "\r\n"); setCmd(""); } };
  if (!online) return <div className="text-zinc-500 text-sm p-6">Agent offline — terminal unavailable.</div>;

  return (
    <div className="bg-black border border-zinc-900 rounded-md overflow-hidden h-[460px] flex flex-col">
      <div className="px-3 py-2 border-b border-zinc-900 flex items-center gap-2 text-xs"><span className={connected ? "text-emerald-400" : "text-zinc-500"}>● {connected ? "connected" : "connecting…"}</span></div>
      <pre ref={outRef} className="flex-1 overflow-y-auto p-3 font-mono text-[12.5px] whitespace-pre-wrap text-zinc-300">{lines || "…"}</pre>
      <form onSubmit={send} className="flex border-t border-zinc-900"><span className="px-2 py-2 text-emerald-400 font-mono">›</span><input value={cmd} onChange={(e) => setCmd(e.target.value)} autoFocus className="flex-1 bg-black text-zinc-200 font-mono text-sm px-1 outline-none" placeholder="type a command…" /></form>
    </div>
  );
}

function Screen({ id, online }) {
  const [frame, setFrame] = useState(null);
  const [control, setControl] = useState(false);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!online) return;
    let ws;
    (async () => {
      const t = await ticket();
      ws = new WebSocket(wsUrl(`/agent/${id}/screen?token=${encodeURIComponent(t)}&interval_ms=500&quality=55`));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onmessage = (e) => { const m = JSON.parse(e.data); if (m.image_b64) setFrame({ src: `data:image/jpeg;base64,${m.image_b64}`, w: m.width, h: m.height, seq: m.seq }); };
    })();
    return () => ws && ws.close();
  }, [id, online]);

  const coords = (e) => { const r = imgRef.current.getBoundingClientRect(); return { x: Math.round((e.clientX - r.left) / r.width * frame.w), y: Math.round((e.clientY - r.top) / r.height * frame.h) }; };
  const send = (ev) => { if (control && wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(ev)); };
  if (!online) return <div className="text-zinc-500 text-sm p-6">Agent offline — remote desktop unavailable.</div>;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 text-sm">
        <span className={connected ? "text-emerald-400" : "text-zinc-500"}>● {connected ? "streaming" : "connecting…"}</span>
        <label className="flex items-center gap-2 text-zinc-300"><input type="checkbox" checked={control} onChange={(e) => setControl(e.target.checked)} />Take control</label>
        <span className="ml-auto font-mono text-xs text-zinc-600">{frame ? `${frame.w}×${frame.h} #${frame.seq}` : "no frame"}</span>
      </div>
      <div tabIndex={0} onKeyDown={(e) => { if (!control) return; e.preventDefault(); e.key.length === 1 ? send({ kind: "text", text: e.key }) : send({ kind: "key", key: e.key }); }}
        className="bg-black border border-zinc-900 rounded-md overflow-hidden" style={{ outline: control ? "2px solid #2563eb" : "none" }}>
        {frame ? <img ref={imgRef} src={frame.src} alt="remote" draggable={false}
          onMouseMove={(e) => control && send({ kind: "move", ...coords(e) })}
          onClick={(e) => control && send({ kind: "click", ...coords(e), button: "left" })}
          onContextMenu={(e) => { e.preventDefault(); control && send({ kind: "click", ...coords(e), button: "right" }); }}
          onDoubleClick={(e) => control && send({ kind: "click", ...coords(e), button: "left", double: true })}
          onWheel={(e) => control && send({ kind: "scroll", ...coords(e), dy: e.deltaY > 0 ? -1 : 1 })}
          style={{ width: "100%", cursor: control ? "crosshair" : "default", userSelect: "none" }} />
          : <div className="p-16 text-center text-zinc-500 text-sm">Waiting for first frame…</div>}
      </div>
    </div>
  );
}
